// Market-detail route metadata (SM-381) — the router-side glue between
// the catalog query cache and src/lib/seo.ts.
//
// WHY THIS IS NON-BLOCKING, and what covers the gap:
//
// A route `head` is computed from `loaderData`, so the obvious shape is a
// loader that awaits the market's catalog row. We deliberately don't: on
// a cold open that adds a full round-trip in front of the page, turning
// today's instant skeleton into a blank wait — a real UX regression paid
// for by metadata nobody in a browser can see anyway (the tab title).
//
// So the loader reads the row SYNCHRONOUSLY from the react-query cache
// and kicks off a background prefetch. Result per audience:
//
//   • Social crawlers (the actual bug) — never reach this code. nginx
//     matches their user-agent and serves the gateway's fully-populated
//     document instead. Always market-specific. See nginx.conf and
//     api-gateway/internal/controller/client/og.go.
//   • A user arriving from the feed / watchlist / search — the row is
//     already cached, so the tab title is market-specific immediately.
//   • A user deep-linking cold — gets the site-default title for this
//     visit; the prefetch warms the cache for the next one.
//
// The one case that stays generic is therefore a cold human deep-link,
// where the title is cosmetic. If we ever want it exact, the fix is a
// blocking loader, and it should be a deliberate latency decision rather
// than a side effect of this card.

import type { QueryClient } from "@tanstack/react-query";
import { fetchCatalogMarket, keys } from "@/lib/queries";
import type { MarketItem } from "@/lib/types";
import { marketHead, type HeadDescriptor } from "@/lib/seo";

export interface MarketMetaLoaderData {
  id: string;
  market: MarketItem | null;
}

interface LoaderArgs {
  params: { id: string };
  context: { queryClient: QueryClient };
}

/**
 * marketMetaLoader hands the route's `head` whatever the cache already
 * knows about this market, and warms the cache for next time. Never
 * throws and never awaits the network: a metadata loader must not be
 * able to fail a page.
 */
export function marketMetaLoader({ params, context }: LoaderArgs): MarketMetaLoaderData {
  const { id } = params;
  const key = keys.catalogMarket(id);
  const cached = context.queryClient.getQueryData<MarketItem>(key) ?? null;

  if (!cached) {
    // Fire-and-forget. `void` is load-bearing: a rejected prefetch (a
    // deleted market, a 502) must not surface as an unhandled rejection
    // or reach the route's error boundary.
    void context.queryClient
      .prefetchQuery({ queryKey: key, queryFn: () => fetchCatalogMarket(id) })
      .catch(() => undefined);
  }

  return { id, market: cached };
}

/**
 * marketHeadFromLoader adapts marketMetaLoader's output into a route
 * `head` function. `path` is a callback because the same market is
 * reachable at two canonical URLs (/markets/:id from the feed,
 * /explore/:id from Explore) and each must self-canonicalize to the URL
 * the visitor is actually on.
 */
export function marketHeadFromLoader(path: (id: string) => string) {
  return ({ loaderData }: { loaderData?: MarketMetaLoaderData }): HeadDescriptor => {
    if (!loaderData) return marketHead("/", null);
    return marketHead(path(loaderData.id), loaderData.market);
  };
}
